<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'next/button/Button.vue';

const props = defineProps({
  // Elemento raiz da página: cada filho vira um bloco do PDF.
  target: { type: Object, default: null },
  pageTitle: { type: String, required: true },
  slug: { type: String, required: true },
  periodLabel: { type: String, default: '' },
  filterLines: { type: Array, default: () => [] },
  notes: { type: Array, default: () => [] },
  legend: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
  // Prepara a página antes da captura (ex.: abrir seções); pode devolver uma
  // função que desfaz a preparação depois.
  beforeExport: { type: Function, default: null },
});

const { t } = useI18n();
const { currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const isExporting = ref(false);

const exportPdf = async () => {
  const now = new Date();
  const fileName = `dashboard-${props.slug}-${format(now, 'yyyy-MM-dd')}.pdf`;

  isExporting.value = true;
  let restorePage = null;
  try {
    restorePage = await props.beforeExport?.();
    const { exportDashboardPdf } = await import('../exportDashboardPdf');
    const filters = props.filterLines.length
      ? props.filterLines.join(' · ')
      : t('DASHBOARD.EXPORT_PDF.NO_FILTERS');

    await exportDashboardPdf({
      target: props.target,
      fileName,
      title: t('DASHBOARD.EXPORT_PDF.TITLE', { page: props.pageTitle }),
      accountName: currentAccount.value.name,
      subtitleLines: [
        `${t('DASHBOARD.EXPORT_PDF.ACCOUNT')}: ${currentAccount.value.name}`,
        `${t('DASHBOARD.EXPORT_PDF.PERIOD')}: ${props.periodLabel}`,
        `${t('DASHBOARD.EXPORT_PDF.FILTERS')}: ${filters}`,
      ],
      generatedLabel: t('DASHBOARD.EXPORT_PDF.GENERATED', {
        user: currentUser.value.name,
        date: format(now, 'dd/MM/yyyy HH:mm'),
      }),
      pageLabel: (page, total) =>
        t('DASHBOARD.EXPORT_PDF.PAGE', { page, total }),
      legendTitle: t('DASHBOARD.EXPORT_PDF.LEGEND'),
      notes: props.notes,
      legend: props.legend,
    });
    useAlert(t('DASHBOARD.EXPORT_PDF.SUCCESS'));
  } catch {
    useAlert(t('DASHBOARD.EXPORT_PDF.ERROR'));
  } finally {
    restorePage?.();
    isExporting.value = false;
  }
};
</script>

<template>
  <Button
    sm
    faded
    slate
    icon="i-lucide-file-down"
    :is-loading="isExporting"
    :disabled="disabled || isExporting || !target"
    :label="
      isExporting
        ? t('DASHBOARD.EXPORT_PDF.EXPORTING')
        : t('DASHBOARD.EXPORT_PDF.BUTTON')
    "
    @click="exportPdf"
  />
</template>
